/**
 * VehiclePositionMap.tsx
 * 車両ポジションマップ — ビジュアル車両位置管理
 * ドラッグ＆ドロップで車両ステータスを変更
 */
import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Paper, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { updateVehicle } from '../api/vehicleService';
import { saveDispatchReservation } from '../api/dispatchService';
import { useReservationData } from '../context/ReservationDataContext';

// --- 点滅アニメーション用CSS ---
const pulseKeyframes = `
@keyframes vehiclePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}`;
if (typeof document !== 'undefined' && !document.getElementById('vehicle-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'vehicle-pulse-style';
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

// --- ゾーン定義 ---
interface ZoneConfig {
  id: string;
  label: string;
  statusId: number;
  gridArea: string;
  color: string;
  borderColor: string;
  columns?: number; // 車両カードの列数（デフォルト: flex wrap）
  droppable?: boolean; // ドロップ可能か（デフォルト: true）
}

const ZONES: ZoneConfig[] = [
  { id: 'airport_1f', label: '空港1Fエリア', statusId: 14, gridArea: 'f', color: '#fff3e0', borderColor: '#e65100', droppable: false },
  { id: 'akamine_south_pu', label: '赤嶺駅 南口\nピックアップ', statusId: 16, gridArea: 'n', color: '#fce4ec', borderColor: '#ad1457' },
  { id: 'akamine_south_do', label: '赤嶺駅 南口\nドロップオフ', statusId: 15, gridArea: 's', color: '#e8eaf6', borderColor: '#3949ab' },
  { id: 'mitsui_p', label: '三井P', statusId: 7, gridArea: 'm', color: '#e8f5e9', borderColor: '#2e7d32' },
  { id: 'sending', label: '空港3F\n（ドロップオフ）', statusId: 10, gridArea: 'b', color: '#e3f2fd', borderColor: '#1976d2' },
  { id: 'pickup', label: '空港1F\n（ピックアップ）', statusId: 14, gridArea: 'c', color: '#fce4ec', borderColor: '#c62828' },
  { id: 'airport', label: '店舗', statusId: 2, gridArea: 'a', color: '#fffde7', borderColor: '#f57f17' },
  { id: 'in_use', label: '利用中\n（返却予定）', statusId: 1, gridArea: 'e', color: '#f3e5f5', borderColor: '#7b1fa2' },
  { id: 'yard', label: 'ヤード', statusId: 4, gridArea: 'd', color: '#efebe9', borderColor: '#4e342e' },
];

// Hゾーン
const H_ZONE: ZoneConfig = {
  id: 'departed_today', label: '貸出', statusId: 1, gridArea: 'h',
  color: '#e8eaf6', borderColor: '#3949ab',
};

// ゾーンごとの車両位置ステータス（ドロップ時にセット）
// 1: to_airport（迎え中/送り中）, 2: to_naha_shop（空港店）, 3: to_yard（ヤード）
const ZONE_LOCATION_STATUS: Record<string, number | null> = {
  pickup: 1,              // 迎え中 → to_airport
  sending: 1,             // 送り中 → to_airport
  akamine_south_pu: 4,    // 赤嶺駅南口P.U → to_akamine_south
  akamine_south_do: 4,    // 赤嶺駅南口D.O → to_akamine_south
  airport: 2,             // 空港店 → to_naha_shop
  yard: 3,                // ヤード → to_yard
};

// Rゾーン（予約リスト、ドロップ不可）
const R_ZONE: ZoneConfig = {
  id: 'reservation_list', label: '予約リスト', statusId: -1, gridArea: 'r',
  color: '#e0f2f1', borderColor: '#00695c', droppable: false,
};

// 予約ステータス除外セット（cancelled, nonreserved, full, done）
const EXCLUDED_RES_STATUSES = new Set(['cancelled', 'nonreserved', 'full', 'done', '4', '5', '6', '2']);

// status_id → zone_id マッピング
const STATUS_TO_ZONE: Record<number, string> = {
  2: 'airport',
  10: 'sending',
  14: 'pickup',
  4: 'yard',
  1: 'in_use',
  12: 'airport',
  15: 'akamine_south_do',
  16: 'akamine_south_pu',
  7: 'mitsui_p',
};

// --- Props ---
interface VehiclePositionMapProps {
  vehicles: Record<string, any>;
  vehicleStatusList: any[];
  locationStatusList?: any[];
  departures: any[];
  returns: any[];
  upcomingStatuses?: Record<string, any>;
  onVehicleClick?: (vehicle: any) => void;
  onStaffClick?: (reservation: any, staffType: string, listType: string) => void;
  onReservationClick?: (reservation: any) => void;
  onOpenLineChat?: (lineUser: any, reservationId: string) => void;
  lineUsers?: any[];
  staffList?: any[];
  userId?: string;
}

// --- ピックアップサブゾーン共通定義（export for ReservationTable） ---
export const PICKUP_SUB_ZONES = [
  { id: 'pickup_contact', label: '連絡あり', airportValue: '未', borderColor: '#00695c' },
  { id: 'pickup_lobby', label: 'ロビー', airportValue: 'ロビー', borderColor: '#1565c0' },
  { id: 'pickup_gate14', label: '14番', airportValue: '14番', borderColor: '#5d4037' },
  { id: 'pickup_shuttle', label: '送迎車', airportValue: '送迎車', borderColor: '#e65100' },
  { id: 'pickup_walkin', label: '直接来店', airportValue: '直接来店', borderColor: '#990700', textColor: '#ffeb3b' },
] as const;

// --- 予約情報の型 ---
interface VehicleResInfo {
  returnRes?: { time: string; customerName: string; reservationId?: string; isToday?: boolean };
  departureRes?: { time: string; customerName: string; reservationId?: string; isToday?: boolean };
  driverName?: string;
  driverBgColor?: string;
  driverTextColor?: string;
}

// --- VehicleCard ---
function VehicleCard({ vehicle, resInfo, isDragging, locStatusMap, lineUserMap, onOpenLineChat }: {
  vehicle: any;
  resInfo?: VehicleResInfo | null;
  isDragging?: boolean;
  locStatusMap?: Record<number, { label: string; bg_color?: string; text_color?: string }>;
  lineUserMap?: Record<string, any>;
  onOpenLineChat?: (lineUser: any, reservationId: string) => void;
}) {
  const bgColor = vehicle.vehicle_type?.bg_color || '#e0e0e0';
  const textColor = vehicle.vehicle_type?.text_color || '#000';
  const statusId = Number(vehicle.status);
  const locStatus = vehicle.location_status ? locStatusMap?.[vehicle.location_status] : null;
  // 利用中(1)/送り中(10) → 返却予約が優先（現在占有中）
  // 空港店(2)/迎え中(14)/ヤード(4) → 貸出予約が優先（次の利用者）
  const isReturnPrimary = statusId === 1 || statusId === 10;
  const primary = isReturnPrimary ? resInfo?.returnRes : resInfo?.departureRes;
  const secondary = isReturnPrimary ? resInfo?.departureRes : resInfo?.returnRes;
  const primaryLabel = isReturnPrimary ? '返却' : '貸出';
  const secondaryLabel = isReturnPrimary ? '貸出' : '返却';

  return (
    <Paper
      elevation={isDragging ? 8 : 1}
      sx={{
        px: 0.5,
        pt: 0.2,
        pb: 0.2,
        borderRadius: 1,
        backgroundColor: bgColor,
        color: textColor,
        cursor: 'grab',
        minWidth: 95,
        maxWidth: 150,
        opacity: isDragging ? 0.8 : 1,
        transform: isDragging ? 'scale(1.05)' : 'none',
        transition: 'transform 0.1s, box-shadow 0.1s',
        userSelect: 'none',
        '&:hover': { boxShadow: 3 },
      }}
    >
      {/* 車両コード行 + LINEアイコン */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2 }}>
          {vehicle.vehicle_code}
        </Typography>
        {(() => {
          const primaryResId = primary?.reservationId || secondary?.reservationId;
          const lineUser = primaryResId && lineUserMap ? lineUserMap[primaryResId] : null;
          if (!lineUser) return null;
          return (
            <span
              style={{
                fontSize: '0.7rem',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenLineChat && primaryResId) {
                  onOpenLineChat(lineUser, primaryResId);
                }
              }}
            >
              💬
            </span>
          );
        })()}
      </div>
      {/* タグ行: 運転者 + 位置ステータス */}
      {(resInfo?.driverName || locStatus) && (
        <div style={{ fontSize: '0.6rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, marginTop: 2, marginBottom: 2 }}>
          {resInfo?.driverName ? (
            <span style={{
              fontWeight: 600,
              backgroundColor: resInfo.driverBgColor || '#e8f5e9',
              color: resInfo.driverTextColor || '#2e7d32',
              border: `1px solid ${resInfo.driverTextColor || '#4caf50'}`,
              borderRadius: '3px',
              padding: '1px 4px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
              {resInfo.driverName.length > 6 ? resInfo.driverName.slice(0, 6) + '..' : resInfo.driverName}
            </span>
          ) : <span />}
          {locStatus && (
            <span style={{
              fontWeight: 600,
              backgroundColor: locStatus.bg_color || '#666',
              color: locStatus.text_color || '#fff',
              borderRadius: '3px',
              padding: '1px 3px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
              {locStatus.label}
            </span>
          )}
        </div>
      )}
      {primary && (() => {
        // 両方の予約がある場合、貸出が当日なら赤表示
        const isDepartureToday = primary && secondary && primaryLabel === '貸出' && resInfo?.departureRes?.isToday;
        // 未来の予約（明日以降）はグレー表示
        const isFuture = !primary.isToday && /^[\d]+\//.test(primary.time);
        const futureColor = isFuture ? '#6b7280' : undefined;
        return (
          <>
            <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.9, color: isDepartureToday ? '#d32f2f' : futureColor || 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }}>
              {primaryLabel} {primary.time}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.8, color: isDepartureToday ? '#d32f2f' : futureColor || 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }} noWrap>
              {primary.customerName}
            </Typography>
          </>
        );
      })()}
      {secondary && (() => {
        const isDepartureToday = primary && secondary && secondaryLabel === '貸出' && resInfo?.departureRes?.isToday;
        return (
          <>
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1.2, opacity: isDepartureToday ? 0.9 : 0.6, color: isDepartureToday ? '#d32f2f' : 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }}>
              {secondaryLabel} {secondary.time}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1.2, opacity: isDepartureToday ? 0.8 : 0.5, color: isDepartureToday ? '#d32f2f' : 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }} noWrap>
              {secondary.customerName}
            </Typography>
          </>
        );
      })()}
      {!primary && !secondary && (
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.5 }}>-</Typography>
      )}
    </Paper>
  );
}

// --- DraggableVehicleCard ---
function DraggableVehicleCard({ vehicle, resInfo, onClick, onLocationClear, locStatusMap, lineUserMap, onOpenLineChat }: {
  vehicle: any;
  resInfo?: VehicleResInfo | null;
  onClick?: () => void;
  onLocationClear?: (vehicle: any) => void;
  locStatusMap?: Record<number, { label: string; bg_color?: string; text_color?: string }>;
  lineUserMap?: Record<string, any>;
  onOpenLineChat?: (lineUser: any, reservationId: string) => void;
}) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `vehicle-${vehicle.id}`,
    data: { vehicle },
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `vehicle-drop-${vehicle.id}`,
    data: { vehicle },
  });
  const locStatus = vehicle.location_status;
  const isMoving = locStatus === 1 || locStatus === 2 || locStatus === 3;

  return (
    <div
      ref={(node) => { setDragRef(node); setDropRef(node); }}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (isMoving && onLocationClear) {
          onLocationClear(vehicle);
        }
      }}
      style={{
        opacity: isDragging ? 0.3 : 1,
        animation: isMoving ? 'vehiclePulse 2s ease-in-out infinite' : 'none',
        outline: isOver && !isDragging ? '2px dashed #3b82f6' : 'none',
        outlineOffset: '2px',
        borderRadius: isOver && !isDragging ? '8px' : undefined,
        transition: 'outline 0.15s',
      }}
    >
      <VehicleCard vehicle={vehicle} resInfo={resInfo} locStatusMap={locStatusMap} lineUserMap={lineUserMap} onOpenLineChat={onOpenLineChat} />
    </div>
  );
}

// --- DraggableReservationCard（予約カード、DnD可能） ---
function DraggableReservationCard({ reservation, onClick, children }: {
  reservation: any;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `reservation-${reservation.id}`,
    data: { reservation },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{ opacity: isDragging ? 0.3 : 1 }}
    >
      <Paper elevation={0} sx={{
        px: 0.6, py: 0.3, borderRadius: 1, cursor: 'grab',
        backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
        opacity: 0.7, '&:hover': { boxShadow: 2, opacity: 1 },
      }}>
        {children}
      </Paper>
    </div>
  );
}

// --- PickupSubZone（ピックアップ内サブエリア、ドロップ可能） ---
function PickupSubZone({ id, label, reservations, onReservationClick, borderColor }: {
  id: string;
  label: string;
  reservations: any[];
  onReservationClick: (r: any) => void;
  borderColor: string;
}) {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <Box ref={setNodeRef} sx={{
      border: `1px solid ${isOver ? '#1976d2' : borderColor}`,
      borderRadius: 1,
      backgroundColor: isOver ? '#bbdefb' : 'rgba(255,255,255,0.5)',
      p: 0.5, flex: '1 1 0', minHeight: 32,
      display: 'flex', flexDirection: 'column',
      transition: 'background-color 0.15s',
      overflow: 'hidden',
    }}>
      <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: borderColor, mb: 0.2, lineHeight: 1, flexShrink: 0 }}>
        {label} {reservations.length > 0 && `(${reservations.length})`}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, flex: 1, overflowY: 'auto' }}>
        {reservations.map((r: any) => {
          const truncName = (r.customer_name || '').length > 6
            ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
          return (
            <DraggableReservationCard key={r.id} reservation={r} onClick={() => onReservationClick(r)}>
              <Typography sx={{ fontSize: '0.5rem', color: '#666', lineHeight: 1.1 }}>
                {dayjs(r.start_date).format('HH:mm')}
              </Typography>
              <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, lineHeight: 1.1 }}>
                {truncName}
              </Typography>
            </DraggableReservationCard>
          );
        })}
      </Box>
    </Box>
  );
}

// --- ReservationListZone（予約リスト、ドロップ可能） ---
function ReservationListZone({ children, count }: { children: React.ReactNode; count: number }) {
  const { isOver, setNodeRef } = useDroppable({ id: 'reservation_list' });
  return (
    <Box ref={setNodeRef} sx={{
      gridArea: 'r',
      border: `2px solid ${isOver ? '#1976d2' : R_ZONE.borderColor}`,
      borderRadius: 1.5,
      backgroundColor: isOver ? '#bbdefb' : R_ZONE.color,
      p: 0.8, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'background-color 0.15s',
    }}>
      <Typography sx={{
        fontSize: '0.65rem', fontWeight: 700, color: R_ZONE.borderColor,
        mb: 0.3, lineHeight: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
      }}>
        予約リスト
        {count > 0 && (
          <Box component="span" sx={{
            backgroundColor: R_ZONE.borderColor, color: '#fff', borderRadius: '50%',
            width: 16, height: 16, display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '0.55rem', fontWeight: 700,
          }}>
            {count}
          </Box>
        )}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, flex: 1, overflowY: 'auto' }}>
        {children}
      </Box>
    </Box>
  );
}

// --- サブグループ ---
interface SubGroup {
  label: string;
  vehicles: any[];
}

// --- DropZone ---
// --- Arrow（矢印、ゾーン辺に配置） ---
const ARROW_STYLE: React.CSSProperties = {
  position: 'absolute',
  fontSize: '1rem',
  fontWeight: 900,
  color: '#94a3b8',
  zIndex: 1,
  pointerEvents: 'none',
  lineHeight: 1,
};

function DropZone({ zone, children, count, subGroups, renderCard, collapsibleLast, flexChildren, arrows, maxVisible }: {
  zone: ZoneConfig;
  children?: React.ReactNode;
  count: number;
  subGroups?: SubGroup[];
  renderCard?: (vehicle: any) => React.ReactNode;
  collapsibleLast?: boolean;
  flexChildren?: boolean;
  arrows?: { position: 'top' | 'bottom' | 'left' | 'right'; label: string }[];
  maxVisible?: number;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: zone.id, disabled: zone.droppable === false });
  const [lastGroupOpen, setLastGroupOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        gridArea: zone.gridArea,
        border: `2px solid ${isOver ? '#1976d2' : zone.borderColor}`,
        borderRadius: 1.5,
        backgroundColor: isOver ? '#bbdefb' : zone.color,
        p: 0.8,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        position: 'relative',
        transition: 'background-color 0.15s, border-color 0.15s',
        overflow: 'visible',
        minHeight: 0,
      }}
    >
      {/* 矢印 */}
      {arrows?.map((a, i) => (
        <span key={i} style={{
          ...ARROW_STYLE,
          ...(a.position === 'top' ? { top: -2, left: '50%', transform: 'translate(-50%, -100%)' } :
            a.position === 'bottom' ? { bottom: -2, left: '50%', transform: 'translate(-50%, 100%)' } :
              a.position === 'left' ? { left: -2, top: '50%', transform: 'translate(-100%, -50%)' } :
                { right: -2, top: '50%', transform: 'translate(100%, -50%)' }),
        }}>
          {a.label}
        </span>
      ))}
      <Typography sx={{
        fontSize: '0.65rem',
        fontWeight: 700,
        color: zone.borderColor,
        mb: 0.3,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        lineHeight: 1.2,
      }}>
        <span style={{ whiteSpace: 'pre-line' }}>{zone.label}</span>
        {count > 0 && (
          <Box component="span" sx={{
            backgroundColor: zone.borderColor,
            color: '#fff',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.55rem',
            fontWeight: 700,
          }}>
            {count}
          </Box>
        )}
      </Typography>
      {subGroups ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {subGroups.map((sg, idx) => {
            const isLast = idx === subGroups.length - 1;
            const isCollapsible = collapsibleLast && isLast && sg.vehicles.length > 0;
            return (
              <Box key={sg.label}>
                {idx > 0 && <Box sx={{ borderTop: '1px dashed', borderColor: zone.borderColor, opacity: 0.3, my: 0.5 }} />}
                <Typography
                  onClick={isCollapsible ? () => setLastGroupOpen(prev => !prev) : undefined}
                  sx={{
                    fontSize: '0.5rem', color: zone.borderColor, opacity: 0.7, mb: 0.3, fontWeight: 600,
                    cursor: isCollapsible ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  {sg.label}（{sg.vehicles.length}）{isCollapsible ? (lastGroupOpen ? ' ▲' : ' ▼') : ''}
                </Typography>
                {(!isCollapsible || lastGroupOpen) && (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))',
                    gap: 0.5,
                    alignContent: 'start',
                  }}>
                    {sg.vehicles.map((v: any) => renderCard ? renderCard(v) : null)}
                  </Box>
                )}
                {sg.vehicles.length === 0 && !isCollapsible && (
                  <Typography sx={{ fontSize: '0.45rem', color: zone.borderColor, opacity: 0.4 }}>-</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{
          ...(flexChildren ? {
            display: 'flex', flexDirection: 'column', gap: 0.5,
          } : {
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))', gap: 0.5, alignContent: 'start',
          }),
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}>
          {(() => {
            const childArray = React.Children.toArray(children);
            const shouldCollapse = maxVisible && childArray.length > maxVisible && !showAll;
            return (
              <>
                {shouldCollapse ? childArray.slice(0, maxVisible) : childArray}
                {maxVisible && childArray.length > maxVisible && (
                  <Typography
                    onClick={() => setShowAll(prev => !prev)}
                    sx={{ fontSize: '0.6rem', color: zone.borderColor, cursor: 'pointer', userSelect: 'none', fontWeight: 600, textAlign: 'center', gridColumn: '1 / -1' }}
                  >
                    {showAll ? `▲ 折りたたむ` : `▼ 残り${childArray.length - maxVisible}台を表示`}
                  </Typography>
                )}
              </>
            );
          })()}
        </Box>
      )}
    </Box>
  );
}

// --- モバイル時のゾーン表示順序 ---
const MOBILE_ZONE_ORDER = ['in_use', 'akamine_south_pu', 'akamine_south_do', 'mitsui_p', 'sending', 'airport', 'pickup', 'airport_1f', 'yard'];

// --- メインコンポーネント ---
export default function VehiclePositionMap({
  vehicles,
  vehicleStatusList,
  departures,
  returns,
  upcomingStatuses,
  onVehicleClick,
  onStaffClick,
  onReservationClick,
  onOpenLineChat,
  lineUsers,
  staffList,
  locationStatusList,
  userId,
}: VehiclePositionMapProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeVehicle, setActiveVehicle] = useState<any>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });
  const [vehicleSwapConfirm, setVehicleSwapConfirm] = useState<{
    sourceVehicle: any; targetVehicle: any;
    sourceRes: any; targetRes: any;
  } | null>(null);
  // 位置ステータスの色マップ
  const locStatusMap = useMemo(() => {
    const map: Record<number, { label: string; bg_color?: string; text_color?: string }> = {};
    (locationStatusList || []).forEach((s: any) => {
      if (s.id) map[s.id] = { label: s.label || '', bg_color: s.bg_color, text_color: s.text_color };
    });
    return map;
  }, [locationStatusList]);

  // LINE ユーザーマップ: reservation_id → lineUser（ReservationTableと同じロジック）
  const lineUserMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (!Array.isArray(lineUsers)) return map;
    lineUsers.forEach((u: any) => {
      const rids = u.reservation_ids || [];
      rids.forEach((rid: any) => {
        if (!String(rid).startsWith('STAFF:')) {
          map[String(rid)] = u;
        }
      });
    });
    return map;
  }, [lineUsers]);

  // 楽観的更新用: vehicle_id → 一時的な statusId（API完了前に即座にゾーン移動）
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, number>>({});
  // 楽観的に貸出エリアに移動した車両ID
  const [optimisticDeparted, setOptimisticDeparted] = useState<Set<string>>(new Set());
  // 予約の楽観的更新用: reservationId → { 到着連絡, 空港 } (null=削除)
  const [optimisticResJson, setOptimisticResJson] = useState<Record<string, { arrival: string | null; airport: string | null }>>({});
  // ドラッグ中の予約カード（DragOverlay用）
  const [activeReservation, setActiveReservation] = useState<any>(null);
  // 予約モーダル
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [isSavingArrival, setIsSavingArrival] = useState(false);
  // 貸出済み確認モーダル（利用中→空港店移動時）
  const [doneConfirm, setDoneConfirm] = useState<{ vehicle: any; returnReservation: any } | null>(null);
  // 車両カードクリックモーダル
  const [selectedVehicleForModal, setSelectedVehicleForModal] = useState<any>(null);

  // Context から全予約を取得（明日の貸出判定用）
  const { reservations: contextReservations } = useReservationData() as any;

  // 明日貸出の車両IDセット（全予約データから直接計算）
  const tomorrowDepIds = useMemo(() => {
    const tomorrow = dayjs().add(1, 'day');
    const ids = new Set<string>();
    if (!Array.isArray(contextReservations)) return ids;
    contextReservations.forEach((r: any) => {
      if (!r.vehicle_id) return;
      if (EXCLUDED_RES_STATUSES.has(String(r.status))) return;
      const s = dayjs(r.start_date);
      if (s.isValid() && s.format('YYYY-MM-DD') === tomorrow.format('YYYY-MM-DD')) {
        ids.add(String(r.vehicle_id));
      }
    });
    return ids;
  }, [contextReservations]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const isExcludedRes = (r: any) => EXCLUDED_RES_STATUSES.has(String(r.status));

  // 除外後の貸出・返却
  const filteredDepartures = useMemo(() => departures.filter((r: any) => !isExcludedRes(r)), [departures]);
  const filteredReturns = useMemo(() => returns.filter((r: any) => !isExcludedRes(r)), [returns]);

  // 予約マップ: 各車両の「返却予約」と「貸出予約」を分けて管理
  const reservationInfoMap = useMemo(() => {
    const map: Record<string, VehicleResInfo> = {};
    const today = dayjs().format('YYYY-MM-DD');

    // スタッフ解決ヘルパー（ReservationTableと同じロジック）
    const resolveDriver = (staffRaw: any, r: any, type: 'departure' | 'return') => {
      if (!staffRaw) return null;
      let staff = (Array.isArray(staffList) ? staffList.find(s => s.id === staffRaw.id) : null) || staffRaw;
      // ID:14（スポットスタッフ）の場合: メモからアサイン名を取得
      if (staff && staff.id === 14) {
        const memo = r.detail?.memo || r.memo || '';
        const assignLabel = type === 'departure' ? '貸出運転者アサイン' : '返却運転者アサイン';
        const memoRegex = new RegExp(`---${assignLabel} [\\d-]+ [\\d:]+---\\n(.+?)\\n---`);
        const match = memo.match(memoRegex);
        if (match) {
          staff = { ...staff, staff_name: match[1], name: match[1] };
        }
      }
      return staff;
    };

    // 返却の運転者（do_staff）
    const returnDriverMap: Record<string, any> = {};
    filteredReturns.forEach((r: any) => {
      if (!r.vehicle_id) return;
      const vid = String(r.vehicle_id);
      if (!map[vid]) map[vid] = {};
      map[vid].returnRes = {
        time: dayjs(r.end_date).format('HH:mm'),
        customerName: r.customer_name || '',
        reservationId: String(r.id),
        isToday: dayjs(r.end_date).format('YYYY-MM-DD') === today,
      };
      const driver = resolveDriver(r.do_staff, r, 'return');
      if (driver) returnDriverMap[vid] = driver;
    });

    // 貸出の運転者（pu_staff）
    const depDriverMap: Record<string, any> = {};
    filteredDepartures.forEach((r: any) => {
      if (!r.vehicle_id) return;
      const vid = String(r.vehicle_id);
      if (!map[vid]) map[vid] = {};
      map[vid].departureRes = {
        time: dayjs(r.start_date).format('HH:mm'),
        customerName: r.customer_name || '',
        reservationId: String(r.id),
        isToday: dayjs(r.start_date).format('YYYY-MM-DD') === today,
      };
      const driver = resolveDriver(r.pu_staff, r, 'departure');
      if (driver) depDriverMap[vid] = driver;
    });

    // ドライバー情報をセット（ステータスに応じて適切な方を選択）
    Object.keys(map).forEach(vid => {
      const vehicle = Object.values(vehicles).find((v: any) => String(v.id) === vid) as any;
      const statusId = vehicle ? Number(vehicle.status) : 0;
      const staff = (statusId === 1 || statusId === 10)
        ? (returnDriverMap[vid] || depDriverMap[vid])
        : (depDriverMap[vid] || returnDriverMap[vid]);
      if (staff) {
        map[vid].driverName = staff.staff_name || staff.name || '';
        map[vid].driverBgColor = staff.bg_color || undefined;
        map[vid].driverTextColor = staff.text_color || undefined;
      }
    });

    // 未来の貸出予約を追加（ヤード「予定なし」等で次回予約を表示するため）
    if (Array.isArray(contextReservations)) {
      const now = dayjs();
      const tomorrow = now.add(1, 'day');
      // 車両ごとに最も近い未来の貸出予約を探す
      const futureDepByVehicle: Record<string, any> = {};
      contextReservations.forEach((r: any) => {
        if (!r.vehicle_id) return;
        if (EXCLUDED_RES_STATUSES.has(String(r.status))) return;
        const s = dayjs(r.start_date);
        if (!s.isValid() || s.isBefore(now, 'day')) return;
        if (s.isSame(now, 'day')) return; // 今日は既にfilteredDeparturesで処理済み
        const vid = String(r.vehicle_id);
        if (!futureDepByVehicle[vid] || s.isBefore(dayjs(futureDepByVehicle[vid].start_date))) {
          futureDepByVehicle[vid] = r;
        }
      });
      Object.entries(futureDepByVehicle).forEach(([vid, r]) => {
        if (!map[vid]) map[vid] = {};
        if (!map[vid].departureRes) {
          const s = dayjs(r.start_date);
          const label = s.isSame(tomorrow, 'day') ? '明日' : s.format('M/D');
          map[vid].departureRes = {
            time: `${label} ${s.format('HH:mm')}`,
            customerName: r.customer_name || '',
            reservationId: String(r.id),
          };
        }
      });
    }

    return map;
  }, [filteredDepartures, filteredReturns, contextReservations, staffList, vehicles]);

  // departing(status=1)の車両を「利用中(E)」と「貸出(H)」に振り分け
  // 返却予約(A)がまだ完了していない → 利用中（Aのために出発中）
  // 返却予約がない or 完了済み → 貸出（Bのために出発中）
  const { departedTodayVehicles, inUseVehiclesFromDeparting } = useMemo(() => {
    const departureVehicleIds = new Set(filteredDepartures.map((r: any) => String(r.vehicle_id)).filter(Boolean));
    const returnVehicleIds = new Set(filteredReturns.map((r: any) => String(r.vehicle_id)).filter(Boolean));

    const departed: any[] = [];
    const inUse: any[] = [];

    Object.values(vehicles).forEach((v: any) => {
      if (v.out_of_use) return;
      if (Number(v.status) !== 1) return;
      const vid = String(v.id);

      if (returnVehicleIds.has(vid)) {
        // 返却予約がまだある → 利用中（返却予約のために出発中）
        inUse.push(v);
      } else if (departureVehicleIds.has(vid)) {
        // 返却予約なし + 貸出予約あり → 貸出（貸出予約のために出発中）
        departed.push(v);
      }
    });

    departed.sort((a: any, b: any) => (a.vehicle_code || '').localeCompare(b.vehicle_code || ''));
    inUse.sort((a: any, b: any) => (a.vehicle_code || '').localeCompare(b.vehicle_code || ''));
    return { departedTodayVehicles: departed, inUseVehiclesFromDeparting: inUse };
  }, [vehicles, filteredDepartures, filteredReturns]);

  // 車両をゾーンにグループ分け
  const vehiclesByZone = useMemo(() => {
    const groups: Record<string, any[]> = {};
    ZONES.forEach(z => { groups[z.id] = []; });

    const vehicleList = Object.values(vehicles);
    const todayVehicleIds = new Set([
      ...filteredDepartures.map((r: any) => String(r.vehicle_id)),
      ...filteredReturns.map((r: any) => String(r.vehicle_id)),
    ].filter(Boolean));

    // H（貸出）とE特別枠（利用中からのdeparting）に表示する車両IDセット
    const departedTodayIds = new Set(departedTodayVehicles.map((v: any) => String(v.id)));
    const inUseDepartingIds = new Set(inUseVehiclesFromDeparting.map((v: any) => String(v.id)));

    vehicleList.forEach((v: any) => {
      if (v.out_of_use) return;
      const vid = String(v.id);
      // 楽観的更新があればそちらを優先
      const statusId = optimisticStatus[vid] ?? Number(v.status);

      // 楽観的に貸出エリアに移動した車両はメインゾーンから除外
      if (optimisticDeparted.has(vid)) return;

      // H（貸出）またはE特別枠に該当する車両はメインゾーンから除外
      if ((departedTodayIds.has(vid) || inUseDepartingIds.has(vid)) && !optimisticStatus[vid]) return;

      const zoneId = STATUS_TO_ZONE[statusId];
      if (!zoneId) return;

      if (todayVehicleIds.has(vid) || ['airport', 'pickup', 'sending', 'yard', 'mitsui_p', 'akamine_south_pu', 'akamine_south_do'].includes(zoneId)) {
        if (groups[zoneId]) {
          groups[zoneId].push(v);
        }
      }
    });

    // 利用中ゾーンに departing かつ返却予約ありの車両を追加
    inUseVehiclesFromDeparting.forEach((v: any) => {
      if (!optimisticStatus[String(v.id)]) {
        groups.in_use = groups.in_use || [];
        groups.in_use.push(v);
      }
    });

    Object.values(groups).forEach(arr => arr.sort((a: any, b: any) =>
      (a.vehicle_code || '').localeCompare(b.vehicle_code || '')
    ));

    return groups;
  }, [vehicles, filteredDepartures, filteredReturns, departedTodayVehicles, inUseVehiclesFromDeparting, optimisticStatus, optimisticDeparted]);

  // 空港店・ヤードのサブグループ（本日貸出/明日貸出/予定なし）
  const buildSubGroups = useCallback((zoneVehicles: any[]): SubGroup[] => {
    const today = dayjs().startOf('day');
    const tomorrow = today.add(1, 'day');
    const todayDep: any[] = [];
    const tomorrowDep: any[] = [];
    const noDep: any[] = [];

    // filteredDepartures から本日貸出の車両IDセット
    const todayDepIds = new Set(filteredDepartures
      .filter((r: any) => dayjs(r.start_date).isSame(today, 'day'))
      .map((r: any) => String(r.vehicle_id))
      .filter(Boolean)
    );

    zoneVehicles.forEach((v: any) => {
      const vid = String(v.id);
      if (todayDepIds.has(vid)) {
        todayDep.push(v);
        return;
      }
      // 明日貸出かどうかを全予約データから判定
      if (tomorrowDepIds.has(vid)) {
        tomorrowDep.push(v);
        return;
      }
      noDep.push(v);
    });

    // ソート: 位置ステータスあり優先 → 時間昇順
    const sortByLocationThenTime = (arr: any[]) => arr.sort((a: any, b: any) => {
      const aHasLoc = a.location_status ? 1 : 0;
      const bHasLoc = b.location_status ? 1 : 0;
      if (aHasLoc !== bHasLoc) return bHasLoc - aHasLoc; // 位置ステータスあり優先
      const infoA = reservationInfoMap[String(a.id)];
      const infoB = reservationInfoMap[String(b.id)];
      const timeA = infoA?.departureRes?.time || infoA?.returnRes?.time || 'zz';
      const timeB = infoB?.departureRes?.time || infoB?.returnRes?.time || 'zz';
      return timeA.localeCompare(timeB);
    });

    return [
      { label: '本日貸出', vehicles: sortByLocationThenTime(todayDep) },
      { label: '明日貸出', vehicles: sortByLocationThenTime(tomorrowDep) },
      { label: '予定なし', vehicles: noDep },
    ];
  }, [filteredDepartures, tomorrowDepIds, reservationInfoMap]);

  const airportSubGroups = useMemo(() => buildSubGroups(vehiclesByZone.airport || []), [vehiclesByZone.airport, buildSubGroups]);
  const yardSubGroups = useMemo(() => buildSubGroups(vehiclesByZone.yard || []), [vehiclesByZone.yard, buildSubGroups]);

  // 本日のbooked予約を「予約リスト」と「ピックアップ（到着連絡済み）」に分ける
  // 楽観的更新（optimisticResJson）を優先参照
  const { waitingReservations, arrivedReservations } = useMemo(() => {
    const all = filteredDepartures
      .filter((r: any) => {
        const s = String(r.status).toLowerCase();
        return s === '1' || s === 'booked';
      })
      .sort((a: any, b: any) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix());

    const waiting: any[] = [];
    const arrived: any[] = [];
    all.forEach((r: any) => {
      const rid = String(r.id);
      const opt = optimisticResJson[rid];
      // 楽観的更新があればそちらを優先
      const hasArrival = opt !== undefined
        ? opt.arrival === '有り'
        : (r.json_data || r.detail?.json_data)?.data?.['到着連絡'] === '有り';
      if (hasArrival) {
        arrived.push(r);
      } else {
        waiting.push(r);
      }
    });
    return { waitingReservations: waiting, arrivedReservations: arrived };
  }, [filteredDepartures, optimisticResJson]);

  // カードレンダー関数
  // 車両位置ステータスクリア（点滅カードをクリック時）
  const handleLocationClear = useCallback(async (vehicle: any) => {
    try {
      await updateVehicle({ id: vehicle.id, location_status: null }, userId);
      setToast({ open: true, message: `${vehicle.vehicle_code} 移動完了`, severity: 'success' });
    } catch {
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  }, [userId]);

  const renderCard = useCallback((v: any) => (
    <DraggableVehicleCard
      key={v.id}
      vehicle={v}
      resInfo={reservationInfoMap[String(v.id)]}
      onClick={() => setSelectedVehicleForModal(v)}
      onLocationClear={handleLocationClear}
      locStatusMap={locStatusMap}
      lineUserMap={lineUserMap}
      onOpenLineChat={onOpenLineChat}
    />
  ), [reservationInfoMap, handleLocationClear, lineUserMap, onOpenLineChat]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as any;
    setActiveVehicle(data?.vehicle || null);
    setActiveReservation(data?.reservation || null);
  }, []);

  // 予約カードのjson_data更新ヘルパー
  const updateReservationJsonField = useCallback(async (reservation: any, key: string, value: string | undefined) => {
    const jd = reservation.json_data || reservation.detail?.json_data || { data: {} };
    const newData = { ...(jd.data || {}) };
    if (value) { newData[key] = value; } else { delete newData[key]; }
    const newJsonData = { ...jd, data: newData };
    await saveDispatchReservation({
      ...reservation,
      json_data: newJsonData,
      detail: { ...(reservation.detail || {}), json_data: newJsonData },
    });
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveVehicle(null);
    setActiveReservation(null);
    const { active, over } = event;
    if (!over) return;

    const data = active.data.current as any;
    const targetZoneId = String(over.id);

    // --- 車両カード同士のスワップ検出 ---
    if (targetZoneId.startsWith('vehicle-drop-') && data.vehicle) {
      const sourceVehicle = data.vehicle;
      const targetVehicle = (over.data?.current as any)?.vehicle;
      if (!targetVehicle || String(sourceVehicle.id) === String(targetVehicle.id)) return;

      const srcVid = String(sourceVehicle.id);
      const tgtVid = String(targetVehicle.id);

      // スワップ不可チェック: 当日の返却が完了していない車両
      const canSwapVehicle = (vid: string) => {
        const ret = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
        if (!ret) return true; // 当日返却予約なし → OK
        const st = String(ret.status);
        return st === '2'; // done → 返却完了 → OK
      };

      if (!canSwapVehicle(srcVid)) {
        setToast({ open: true, message: `${sourceVehicle.vehicle_code} はまだ返却が完了していないため入替できません`, severity: 'warning' });
        return;
      }
      if (!canSwapVehicle(tgtVid)) {
        setToast({ open: true, message: `${targetVehicle.vehicle_code} はまだ返却が完了していないため入替できません`, severity: 'warning' });
        return;
      }

      // スワップ対象の予約を取得（貸出予約 = これから使う予約）
      const srcRes = filteredDepartures.find((r: any) => String(r.vehicle_id) === srcVid);
      const tgtRes = filteredDepartures.find((r: any) => String(r.vehicle_id) === tgtVid);

      if (!srcRes && !tgtRes) {
        setToast({ open: true, message: '入替対象の予約がありません', severity: 'warning' });
        return;
      }

      setVehicleSwapConfirm({ sourceVehicle, targetVehicle, sourceRes: srcRes, targetRes: tgtRes });
      return;
    }

    // --- 予約カードのDnD ---
    if (data?.reservation) {
      const r = data.reservation;
      // ピックアップ系ゾーンへのドロップ
      const pickupTargets: Record<string, string> = { 'airport_1f': '未' };
      PICKUP_SUB_ZONES.forEach(sz => { pickupTargets[sz.id] = sz.airportValue; });
      const rid = String(r.id);

      // 予約リストへ戻す
      if (targetZoneId === 'reservation_list') {
        // 楽観的更新: 即座に予約リストに移動
        setOptimisticResJson(prev => ({ ...prev, [rid]: { arrival: null, airport: null } }));
        try {
          const jd = r.json_data || r.detail?.json_data || { data: {} };
          const newData = { ...(jd.data || {}) };
          delete newData['到着連絡'];
          delete newData['空港'];
          const newJsonData = { ...jd, data: newData };
          await saveDispatchReservation({
            ...r,
            json_data: newJsonData,
            detail: { ...(r.detail || {}), json_data: newJsonData },
          });
          setToast({ open: true, message: `${r.customer_name} → 予約リスト`, severity: 'success' });
        } catch {
          setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
          setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
        }
        setTimeout(() => { setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; }); }, 5000);
        return;
      }

      const airportValue = pickupTargets[targetZoneId];
      if (airportValue !== undefined) {
        // 楽観的更新: 即座にピックアップ内サブゾーンに移動
        setOptimisticResJson(prev => ({ ...prev, [rid]: { arrival: '有り', airport: airportValue } }));
        try {
          const jd = r.json_data || r.detail?.json_data || { data: {} };
          const newData = { ...(jd.data || {}), '到着連絡': '有り', '空港': airportValue };
          const newJsonData = { ...jd, data: newData };
          await saveDispatchReservation({
            ...r,
            json_data: newJsonData,
            detail: { ...(r.detail || {}), json_data: newJsonData },
          });
          const label = airportValue === '未' ? '連絡あり' : airportValue;
          setToast({ open: true, message: `${r.customer_name} → ${label}`, severity: 'success' });
        } catch {
          setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
          setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
        }
        setTimeout(() => { setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; }); }, 5000);
      }
      return;
    }

    // --- 車両カードのDnD ---
    const vehicle = data?.vehicle;
    if (!vehicle) return;

    const vid = String(vehicle.id);

    // 本日貸出済み(H)へドロップ → 車両=departing(1) + 紐づく貸出予約=in_use(3)
    if (targetZoneId === 'departed_today') {
      setOptimisticStatus(prev => ({ ...prev, [vid]: 1 }));
      setOptimisticDeparted(prev => new Set(prev).add(vid));
      try {
        await updateVehicle({ id: vehicle.id, status: 1, location_status: null }, userId);
        // 紐づく本日の貸出予約を in_use(3) に更新
        const linkedDeparture = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedDeparture) {
          await saveDispatchReservation({ ...linkedDeparture, status: 3, status_code: 'in_use' });
        }
        setToast({ open: true, message: `${vehicle.vehicle_code} → 出発中（予約: 利用中）`, severity: 'success' });
      } catch (error) {
        console.error('Departed today update failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
        setOptimisticDeparted(prev => { const next = new Set(prev); next.delete(vid); return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
        setOptimisticDeparted(prev => { const next = new Set(prev); next.delete(vid); return next; });
      }, 5000);
      return;
    }

    // 空港3F(ドロップオフ)へドロップ → 車両=送り中(10) + location_status=1 + 返却予約=done(2) + 運転者モーダル
    if (targetZoneId === 'sending') {
      setOptimisticStatus(prev => ({ ...prev, [vid]: 10 }));
      try {
        await updateVehicle({ id: vehicle.id, status: 10, location_status: 1 }, userId);
        const linkedReturn = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedReturn) {
          await saveDispatchReservation({ ...linkedReturn, status: 2, status_code: 'done' });
          if (onStaffClick) {
            setTimeout(() => onStaffClick(linkedReturn, 'driver', 'return'), 300);
          }
        }
        setToast({ open: true, message: `${vehicle.vehicle_code} → 空港3F（予約: 貸出済み）`, severity: 'success' });
      } catch (error) {
        console.error('Drop-off update failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
      return;
    }

    // 利用中(E, status=1)→空港店(A, status=2) → 確認モーダル
    if (targetZoneId === 'airport' && Number(vehicle.status) === 1) {
      const linkedReturn = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
      // まず車両ステータスだけ空港店に変更
      setOptimisticStatus(prev => ({ ...prev, [vid]: 2 }));
      try {
        await updateVehicle({ id: vehicle.id, status: 2, location_status: 2 }, userId);
        if (linkedReturn) {
          setDoneConfirm({ vehicle, returnReservation: linkedReturn });
        } else {
          setToast({ open: true, message: `${vehicle.vehicle_code} → 空港店`, severity: 'success' });
        }
      } catch (error) {
        console.error('Airport return failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
      return;
    }

    const targetZone = ZONES.find(z => z.id === targetZoneId);
    if (!targetZone) return;

    if (Number(vehicle.status) === targetZone.statusId) return;

    setOptimisticStatus(prev => ({ ...prev, [vid]: targetZone.statusId }));

    try {
      const locStatus = ZONE_LOCATION_STATUS[targetZoneId] ?? null;
      await updateVehicle({ id: vehicle.id, status: targetZone.statusId, location_status: locStatus }, userId);
      setToast({ open: true, message: `${vehicle.vehicle_code} → ${targetZone.label}`, severity: 'success' });

      // 迎え中(pickup)へドロップ → 貸出予約の運転者モーダル
      if (targetZoneId === 'pickup' && onStaffClick) {
        const linkedDeparture = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedDeparture) {
          setTimeout(() => onStaffClick(linkedDeparture, 'driver', 'departure'), 300);
        }
      }
      // 送り中(sending)へドロップ → 返却予約の運転者モーダル
      if (targetZoneId === 'sending' && onStaffClick) {
        const linkedReturn = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedReturn) {
          setTimeout(() => onStaffClick(linkedReturn, 'driver', 'return'), 300);
        }
      }
    } catch (error) {
      console.error('Vehicle status update failed:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }
    setTimeout(() => {
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }, 5000);
  }, [userId, updateReservationJsonField, filteredDepartures, filteredReturns, onStaffClick]);

  return (
    <>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* メインマップ */}
        {isMobile ? (
          // --- モバイル: 縦並びレイアウト ---
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
            {MOBILE_ZONE_ORDER.map(zoneId => {
              const zone = ZONES.find(z => z.id === zoneId);
              if (!zone) return null;
              const isSubGroupZone = zone.id === 'airport' || zone.id === 'yard';
              const sg = zone.id === 'airport' ? airportSubGroups : zone.id === 'yard' ? yardSubGroups : undefined;
              return (
                <DropZone
                  key={zone.id}
                  zone={{ ...zone, gridArea: '' }}
                  count={vehiclesByZone[zone.id]?.length || 0}
                  subGroups={isSubGroupZone ? sg : undefined}
                  maxVisible={zone.id === 'in_use' ? 10 : undefined}
                  renderCard={isSubGroupZone ? renderCard : undefined}
                  collapsibleLast={zone.id === 'yard'}
                  flexChildren={zone.id === 'airport_1f'}
                >
                  {!isSubGroupZone && zone.id === 'airport_1f' && (() => {
                    const getAirportValue = (r: any) => {
                      const opt = optimisticResJson[String(r.id)];
                      if (opt !== undefined) return opt.airport || '未';
                      const jd = r.json_data || r.detail?.json_data;
                      return jd?.data?.['空港'] || '未';
                    };
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minHeight: 0 }}>
                        {PICKUP_SUB_ZONES.map(sz => {
                          const filtered = arrivedReservations.filter((r: any) => getAirportValue(r) === sz.airportValue);
                          return (
                            <PickupSubZone key={sz.id} id={sz.id} label={sz.label} reservations={filtered}
                              onReservationClick={(r) => setSelectedReservation(r)} borderColor={sz.borderColor} />
                          );
                        })}
                      </Box>
                    );
                  })()}
                  {!isSubGroupZone && zone.id !== 'airport_1f' && (vehiclesByZone[zone.id] || []).map((v: any) => (
                    <DraggableVehicleCard
                      key={v.id}
                      vehicle={v}
                      resInfo={reservationInfoMap[String(v.id)]}
                      onClick={() => setSelectedVehicleForModal(v)}
                      onLocationClear={handleLocationClear}
                      locStatusMap={locStatusMap}
                      lineUserMap={lineUserMap}
                      onOpenLineChat={onOpenLineChat}
                    />
                  ))}
                </DropZone>
              );
            })}

            {/* H: 貸出 */}
            <DropZone zone={{ ...H_ZONE, gridArea: '' }} count={departedTodayVehicles.length + optimisticDeparted.size}>
              {[...departedTodayVehicles, ...Object.values(vehicles).filter((v: any) => optimisticDeparted.has(String(v.id)) && !departedTodayVehicles.some((d: any) => d.id === v.id))].map((v: any) => (
                <DraggableVehicleCard
                  key={v.id}
                  vehicle={v}
                  resInfo={reservationInfoMap[String(v.id)]}
                  onClick={() => setSelectedVehicleForModal(v)}
                  onLocationClear={handleLocationClear}
                  locStatusMap={locStatusMap}
                  lineUserMap={lineUserMap}
                  onOpenLineChat={onOpenLineChat}
                />
              ))}
            </DropZone>

            {/* 区切り */}
            <Box sx={{ borderTop: '2px dashed #ccc', my: 1 }} />

            {/* 予約リスト */}
            <ReservationListZone count={waitingReservations.length}>
              {waitingReservations.map((r: any) => {
                const truncName = (r.customer_name || '').length > 6
                  ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
                return (
                  <DraggableReservationCard key={r.id} reservation={r} onClick={() => setSelectedReservation(r)}>
                    <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
                      {dayjs(r.start_date).format('HH:mm')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
                      {truncName}
                    </Typography>
                  </DraggableReservationCard>
                );
              })}
              {waitingReservations.length === 0 && (
                <Typography sx={{ fontSize: '0.5rem', color: '#999', opacity: 0.6 }}>なし</Typography>
              )}
            </ReservationListZone>
          </Box>
        ) : (
          // --- PC: グリッドレイアウト ---
          <Box sx={{
            display: 'grid',
            gap: 1,
            gridTemplateAreas: `
              "r f b b b b b e"
              "r f c n . s m e"
              "r f c a a a a e"
              "r f d d d d d e"
            `,
            gridTemplateColumns: '105px 120px 120px 1fr 30px 1fr 120px 120px',
            gridTemplateRows: 'minmax(80px, auto) auto 1fr auto',
            minHeight: 200,
            p: 1,
          }}>
            {ZONES.map(zone => {
              const isSubGroupZone = zone.id === 'airport' || zone.id === 'yard';
              const sg = zone.id === 'airport' ? airportSubGroups : zone.id === 'yard' ? yardSubGroups : undefined;
              const zoneArrows =
                zone.id === 'airport' ? [
                  { position: 'left' as const, label: '←' },
                  { position: 'right' as const, label: '←' },
                  { position: 'bottom' as const, label: '↑↓' },
                ] :
                  zone.id === 'sending' ? [
                    { position: 'right' as const, label: '←' },
                  ] :
                    zone.id === 'pickup' ? [
                      { position: 'top' as const, label: '↓' },
                    ] :
                      zone.id === 'akamine_south_pu' ? [
                        { position: 'right' as const, label: '↓↑' },
                      ] : undefined;
              return (
                <DropZone
                  key={zone.id}
                  zone={zone}
                  count={vehiclesByZone[zone.id]?.length || 0}
                  subGroups={isSubGroupZone ? sg : undefined}
                  renderCard={isSubGroupZone ? renderCard : undefined}
                  collapsibleLast={zone.id === 'yard'}
                  maxVisible={zone.id === 'in_use' ? 10 : undefined}
                  arrows={zoneArrows}
                  flexChildren={zone.id === 'airport_1f'}
                >
                  {!isSubGroupZone && zone.id === 'airport_1f' && (() => {
                    // ピックアップ内サブゾーン分け（楽観的更新を優先参照）
                    const getAirportValue = (r: any) => {
                      const opt = optimisticResJson[String(r.id)];
                      if (opt !== undefined) return opt.airport || '未';
                      const jd = r.json_data || r.detail?.json_data;
                      return jd?.data?.['空港'] || '未';
                    };
                    const contactOnly = arrivedReservations.filter((r: any) => {
                      return getAirportValue(r) === '未';
                    });
                    const lobby = arrivedReservations.filter((r: any) => getAirportValue(r) === 'ロビー');
                    const gate14 = arrivedReservations.filter((r: any) => getAirportValue(r) === '14番');
                    const shuttle = arrivedReservations.filter((r: any) => getAirportValue(r) === '送迎車');
                    const walkin = arrivedReservations.filter((r: any) => getAirportValue(r) === '直接来店');
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minHeight: 0 }}>
                        <PickupSubZone id="pickup_contact" label="連絡あり" reservations={contactOnly}
                          onReservationClick={(r) => setSelectedReservation(r)} borderColor="#00695c" />
                        <PickupSubZone id="pickup_lobby" label="ロビー" reservations={lobby}
                          onReservationClick={(r) => setSelectedReservation(r)} borderColor="#1565c0" />
                        <PickupSubZone id="pickup_gate14" label="14番" reservations={gate14}
                          onReservationClick={(r) => setSelectedReservation(r)} borderColor="#5d4037" />
                        <PickupSubZone id="pickup_shuttle" label="送迎車" reservations={shuttle}
                          onReservationClick={(r) => setSelectedReservation(r)} borderColor="#e65100" />
                        <PickupSubZone id="pickup_walkin" label="直接来店" reservations={walkin}
                          onReservationClick={(r) => setSelectedReservation(r)} borderColor="#6a1b9a" />
                      </Box>
                    );
                  })()}
                  {!isSubGroupZone && zone.id !== 'airport_1f' && (vehiclesByZone[zone.id] || []).map((v: any) => (
                    <DraggableVehicleCard
                      key={v.id}
                      vehicle={v}
                      resInfo={reservationInfoMap[String(v.id)]}
                      onClick={() => setSelectedVehicleForModal(v)}
                      onLocationClear={handleLocationClear}
                      locStatusMap={locStatusMap}
                      lineUserMap={lineUserMap}
                      onOpenLineChat={onOpenLineChat}
                    />
                  ))}
                </DropZone>
              );
            })}

            {/* R: 予約リスト（本日のbooked予約、ドロップで戻し可能） */}
            <ReservationListZone count={waitingReservations.length}>
              {waitingReservations.map((r: any) => {
                const truncName = (r.customer_name || '').length > 6
                  ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
                return (
                  <DraggableReservationCard key={r.id} reservation={r} onClick={() => setSelectedReservation(r)}>
                    <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
                      {dayjs(r.start_date).format('HH:mm')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
                      {truncName}
                    </Typography>
                  </DraggableReservationCard>
                );
              })}
              {waitingReservations.length === 0 && (
                <Typography sx={{ fontSize: '0.5rem', color: '#999', opacity: 0.6 }}>なし</Typography>
              )}
            </ReservationListZone>

          </Box>
        )}

        {/* H: 貸出（常に表示、モバイル時はモバイルレイアウト内に含まれるのでPC版のみ） */}
        {!isMobile && (
          <Box sx={{ mt: 0.5, p: 1, minHeight: 120 }}>
            <DropZone zone={H_ZONE} count={departedTodayVehicles.length + optimisticDeparted.size}>
              {[...departedTodayVehicles, ...Object.values(vehicles).filter((v: any) => optimisticDeparted.has(String(v.id)) && !departedTodayVehicles.some((d: any) => d.id === v.id))].map((v: any) => (
                <DraggableVehicleCard
                  key={v.id}
                  vehicle={v}
                  resInfo={reservationInfoMap[String(v.id)]}
                  onClick={() => setSelectedVehicleForModal(v)}
                  onLocationClear={handleLocationClear}
                  locStatusMap={locStatusMap}
                  lineUserMap={lineUserMap}
                  onOpenLineChat={onOpenLineChat}
                />
              ))}
            </DropZone>
          </Box>
        )}

        <DragOverlay>
          {activeVehicle && (
            <VehicleCard
              vehicle={activeVehicle}
              resInfo={reservationInfoMap[String(activeVehicle.id)]}
              isDragging
              locStatusMap={locStatusMap}
            />
          )}
          {activeReservation && (
            <Paper elevation={8} sx={{
              px: 0.6, py: 0.3, borderRadius: 1,
              backgroundColor: '#fff', border: '1.5px solid #00695c',
              transform: 'scale(1.05)', cursor: 'grabbing',
            }}>
              <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
                {dayjs(activeReservation.start_date).format('HH:mm')}
              </Typography>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
                {(activeReservation.customer_name || '').length > 6
                  ? (activeReservation.customer_name || '').slice(0, 6) + '...'
                  : (activeReservation.customer_name || '')}
              </Typography>
            </Paper>
          )}
        </DragOverlay>
      </DndContext>

      {/* 車両スワップ確認Dialog */}
      <Dialog open={Boolean(vehicleSwapConfirm)} onClose={() => setVehicleSwapConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>車両の入替確認</DialogTitle>
        <DialogContent>
          {vehicleSwapConfirm && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={700}>
                  {vehicleSwapConfirm.sourceRes?.customer_name || '予約なし'}
                </Typography>
                <Typography variant="h6" fontWeight={800} color="primary">
                  {vehicleSwapConfirm.sourceVehicle.vehicle_code}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ color: '#94a3b8' }}>⇄</Typography>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={700}>
                  {vehicleSwapConfirm.targetRes?.customer_name || '予約なし'}
                </Typography>
                <Typography variant="h6" fontWeight={800} color="primary">
                  {vehicleSwapConfirm.targetVehicle.vehicle_code}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleSwapConfirm(null)}>キャンセル</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!vehicleSwapConfirm) return;
              const { sourceVehicle, targetVehicle, sourceRes, targetRes } = vehicleSwapConfirm;
              setVehicleSwapConfirm(null);
              try {
                const saves: Promise<any>[] = [];
                if (sourceRes) saves.push(saveDispatchReservation({ ...sourceRes, vehicle_id: String(targetVehicle.id) }));
                if (targetRes) saves.push(saveDispatchReservation({ ...targetRes, vehicle_id: String(sourceVehicle.id) }));
                await Promise.all(saves);
                setToast({ open: true, message: '車両を入替しました', severity: 'success' });
              } catch (err) {
                console.error('Vehicle swap failed:', err);
                setToast({ open: true, message: '車両の入替に失敗しました', severity: 'error' });
              }
            }}
          >
            入替する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* 予約モーダル */}
      <Dialog
        open={!!selectedReservation}
        onClose={() => setSelectedReservation(null)}
        maxWidth="xs"
        fullWidth
      >
        {selectedReservation && (() => {
          const r = selectedReservation;
          const jsonData = r.json_data || r.detail?.json_data;
          const hasArrival = jsonData?.data?.['到着連絡'] === '有り';
          const vehicleObj = Object.values(vehicles).find((veh: any) => String(veh.id) === String(r.vehicle_id));
          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>
                {r.customer_name}
              </DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    貸出: {dayjs(r.start_date).format('HH:mm')}
                  </Typography>
                  {vehicleObj && (
                    <Typography variant="body2" color="text.secondary">
                      車両: {(vehicleObj as any).vehicle_code}
                    </Typography>
                  )}
                  <Button
                    variant={hasArrival ? 'contained' : 'outlined'}
                    color={hasArrival ? 'success' : 'inherit'}
                    fullWidth
                    disabled={isSavingArrival}
                    startIcon={isSavingArrival ? <CircularProgress size={16} color="inherit" /> : undefined}
                    onClick={async () => {
                      setIsSavingArrival(true);
                      try {
                        const currentJsonData = jsonData || { data: {} };
                        const newValue = hasArrival ? undefined : '有り';
                        const newData = { ...(currentJsonData.data || {}) };
                        if (newValue) {
                          newData['到着連絡'] = newValue;
                          if (!newData['空港']) newData['空港'] = '未';
                        } else {
                          delete newData['到着連絡'];
                          delete newData['空港'];
                        }
                        const newJsonData = { ...currentJsonData, data: newData };
                        await saveDispatchReservation({
                          ...r,
                          json_data: newJsonData,
                          detail: { ...(r.detail || {}), json_data: newJsonData },
                        });
                        setToast({
                          open: true,
                          message: hasArrival ? `${r.customer_name} 到着連絡を取消` : `${r.customer_name} 到着連絡を記録`,
                          severity: 'success',
                        });
                        setSelectedReservation(null);
                      } catch {
                        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                      } finally {
                        setIsSavingArrival(false);
                      }
                    }}
                    sx={{ mt: 1 }}
                  >
                    {hasArrival ? '到着連絡: ON（クリックで取消）' : '到着連絡'}
                  </Button>
                  {/* ピックアップサブゾーン選択 */}
                  {hasArrival && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {PICKUP_SUB_ZONES.filter(sz => sz.airportValue !== '未').map(sz => {
                        const currentAirport = jsonData?.data?.['空港'];
                        const isActive = currentAirport === sz.airportValue;
                        return (
                          <Button
                            key={sz.id}
                            variant={isActive ? 'contained' : 'outlined'}
                            size="small"
                            disabled={isSavingArrival}
                            onClick={async () => {
                              setIsSavingArrival(true);
                              try {
                                const currentJsonData = jsonData || { data: {} };
                                const newData = { ...(currentJsonData.data || {}), '空港': isActive ? '未' : sz.airportValue };
                                const newJsonData = { ...currentJsonData, data: newData };
                                await saveDispatchReservation({
                                  ...r,
                                  json_data: newJsonData,
                                  detail: { ...(r.detail || {}), json_data: newJsonData },
                                });
                                setToast({ open: true, message: `${r.customer_name} → ${sz.label}`, severity: 'success' });
                                setSelectedReservation(null);
                              } catch {
                                setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                              } finally {
                                setIsSavingArrival(false);
                              }
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              ...(isActive ? { backgroundColor: sz.borderColor, color: (sz as any).textColor || '#fff', '&:hover': { backgroundColor: sz.borderColor, filter: 'brightness(0.9)' } } : { borderColor: sz.borderColor, color: sz.borderColor }),
                            }}
                          >
                            {sz.label}
                          </Button>
                        );
                      })}
                      {jsonData?.data?.['空港'] && jsonData.data['空港'] !== '未' && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="inherit"
                          disabled={isSavingArrival}
                          onClick={async () => {
                            setIsSavingArrival(true);
                            try {
                              const currentJsonData = jsonData || { data: {} };
                              const newData = { ...(currentJsonData.data || {}), '空港': '未' };
                              const newJsonData = { ...currentJsonData, data: newData };
                              await saveDispatchReservation({
                                ...r,
                                json_data: newJsonData,
                                detail: { ...(r.detail || {}), json_data: newJsonData },
                              });
                              setToast({ open: true, message: `${r.customer_name} → 連絡あり`, severity: 'success' });
                              setSelectedReservation(null);
                            } catch {
                              setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                            } finally {
                              setIsSavingArrival(false);
                            }
                          }}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          クリア
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedReservation(null)}>閉じる</Button>
                <Button variant="outlined" onClick={() => {
                  setSelectedReservation(null);
                  if (vehicleObj) onVehicleClick?.(vehicleObj);
                }}>
                  車両詳細
                </Button>
                <Button variant="outlined" onClick={() => {
                  setSelectedReservation(null);
                  onReservationClick?.(r);
                }}>
                  予約詳細
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* 貸出済み確認モーダル（利用中→空港店移動時） */}
      <Dialog open={!!doneConfirm} onClose={() => setDoneConfirm(null)} maxWidth="xs" fullWidth>
        {doneConfirm && (
          <>
            <DialogTitle sx={{ pb: 1 }}>貸出済みにしますか？</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {doneConfirm.vehicle.vehicle_code} の返却予約
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {doneConfirm.returnReservation.customer_name}（返却: {dayjs(doneConfirm.returnReservation.end_date).format('HH:mm')}）
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setDoneConfirm(null);
                setToast({ open: true, message: `${doneConfirm.vehicle.vehicle_code} → 空港店`, severity: 'success' });
              }}>
                いいえ
              </Button>
              <Button variant="contained" color="primary" onClick={async () => {
                const r = doneConfirm.returnReservation;
                try {
                  await saveDispatchReservation({ ...r, status: 2, status_code: 'done' });
                  setToast({ open: true, message: `${doneConfirm.vehicle.vehicle_code} → 空港店（予約: 貸出済み）`, severity: 'success' });
                } catch {
                  setToast({ open: true, message: '予約の更新に失敗しました', severity: 'error' });
                }
                setDoneConfirm(null);
              }}>
                はい、貸出済みにする
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 車両カードクリックモーダル */}
      <Dialog open={!!selectedVehicleForModal} onClose={() => setSelectedVehicleForModal(null)} maxWidth="xs" fullWidth>
        {selectedVehicleForModal && (() => {
          const v = selectedVehicleForModal;
          const vid = String(v.id);
          const linkedDep = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
          const linkedRet = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
          const linked = linkedDep || linkedRet;
          const listType = linkedDep ? 'departure' : 'return';
          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>
                {v.vehicle_code}
                {v.vehicle_type?.vehicle_type_name && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {v.vehicle_type.vehicle_type_name}
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                {linked && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {linkedDep ? '貸出' : '返却'}: {dayjs(linkedDep ? linked.start_date : linked.end_date).format('HH:mm')} {linked.customer_name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {linked && onStaffClick && (
                    <Button variant="outlined" size="small" onClick={() => {
                      setSelectedVehicleForModal(null);
                      onStaffClick(linked, 'driver', listType);
                    }}>
                      運転者
                    </Button>
                  )}
                  <Button variant="outlined" size="small" onClick={() => {
                    setSelectedVehicleForModal(null);
                    onVehicleClick?.(v);
                  }}>
                    車両詳細
                  </Button>
                </Box>
                {/* 車両位置ステータス選択 */}
                <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #eee' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>車両位置</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {[
                      { id: 1, label: '→空港', color: '#1565c0' },
                      { id: 2, label: '→空港店', color: '#e65100' },
                      { id: 3, label: '→ヤード', color: '#4e342e' },
                    ].map(ls => {
                      const isActive = v.location_status === ls.id;
                      return (
                        <Button
                          key={ls.id}
                          variant={isActive ? 'contained' : 'outlined'}
                          size="small"
                          onClick={async () => {
                            try {
                              const newLocStatus = isActive ? null : ls.id;
                              await updateVehicle({ id: v.id, location_status: newLocStatus }, userId);
                              setToast({ open: true, message: `${v.vehicle_code} ${isActive ? '位置クリア' : ls.label}`, severity: 'success' });
                              setSelectedVehicleForModal(null);
                            } catch {
                              setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                            }
                          }}
                          sx={{
                            fontSize: '0.7rem',
                            ...(isActive ? { backgroundColor: ls.color, '&:hover': { backgroundColor: ls.color, filter: 'brightness(0.9)' } } : { borderColor: ls.color, color: ls.color }),
                          }}
                        >
                          {ls.label}
                        </Button>
                      );
                    })}
                    {v.location_status && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="inherit"
                        onClick={async () => {
                          try {
                            await updateVehicle({ id: v.id, location_status: null }, userId);
                            setToast({ open: true, message: `${v.vehicle_code} 位置クリア`, severity: 'success' });
                            setSelectedVehicleForModal(null);
                          } catch {
                            setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                          }
                        }}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        クリア
                      </Button>
                    )}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedVehicleForModal(null)}>閉じる</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </>
  );
}
